import React, { FormEvent, useCallback, useState } from 'react';
import { Button, CodeEditor, Input, Modal } from '@cognitiv/cyprus-ui';
import { useAppDispatch, useAppSelector } from 'ducks/hooks';
import { createJobDefinition } from 'ducks/job-definitions/operators';
import { selectCreateJobDefinition } from 'ducks/modals/selectors';
import { setModal } from 'ducks/modals/slices';
import { ERROR_LEVEL } from 'ducks/types/common-types';
import { JobDefinitionsCreatePayload } from 'ducks/types/payload-types';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import { useValidate } from 'hooks/useValidation';
import { create_validation, default_job_definition } from 'modals/job-definition/constants';
import { shallowEqual } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getValidationError, handleError } from 'utils/errors';

import cn from 'modals/job-definition/components/Components.module.scss';

interface ParamsProps {
  project_id?: number;
}

export const CreateJobDefinition = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { theme } = useSwitchTheme();
  const { project_id }: ParamsProps = useParams();

  const create_job_definition = useAppSelector(selectCreateJobDefinition, shallowEqual);
  const [form, setForm] = useState<JobDefinitionsCreatePayload>({ ...default_job_definition });
  const { errors, validate, resetFieldError } = useValidate(form, create_validation);

  const closeModal = useCallback(() => {
    dispatch(setModal({ create_job_definition: false }));
  }, [dispatch]);

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const is_valid = validate();

      if (!is_valid) return;

      try {
        if (!project_id) return;

        const res: any = await createJobDefinition(project_id, form);
        if (res?.error) return;

        closeModal();
        navigate(`/projects/${project_id}/job_definitions/all/${res}`);
      } catch (err) {
        handleError(err);
      }
    },
    [validate, project_id, form, closeModal, navigate],
  );

  const onChange = useCallback(
    (item: Partial<JobDefinitionsCreatePayload>) => {
      resetFieldError(item);
      setForm((form) => ({ ...form, ...item }));
    },
    [resetFieldError],
  );

  return (
    <Modal
      name="create_job_definition"
      show={create_job_definition}
      padding={0}
      width={750}
      close_box={50}
      onClose={closeModal}
      standard={false}
    >
      <h2
        className={cn.header}
        style={{
          color: theme.font_color,
          backgroundColor: theme.element_background,
          borderBottom: theme.border,
        }}
      >
        Create Job Definition
      </h2>
      <form className={cn.body} style={{ background: theme.element_background }} onSubmit={onSubmit}>
        <Input
          auto_focus
          label="Job Definition Name"
          value={form.job_definition_name}
          onChange={(item: string) => onChange({ job_definition_name: item })}
          error_message={errors.find((error) => error.key === 'job_definition_name')?.error}
          error_level={
            getValidationError<JobDefinitionsCreatePayload>({ errors, key: 'job_definition_name' })
              ? ERROR_LEVEL.ERROR
              : undefined
          }
        />
        <Input
          label="Docker Image"
          value={form.docker_image}
          margin="10px 0px 0px 0px"
          onChange={(item: string) => onChange({ docker_image: item })}
          error_message={errors.find((error) => error.key === 'docker_image')?.error}
          error_level={
            getValidationError<JobDefinitionsCreatePayload>({ errors, key: 'docker_image' })
              ? ERROR_LEVEL.ERROR
              : undefined
          }
        />
        <div className={cn.removeShadow}>
          <CodeEditor
            label="Startup Command"
            margin="10px 0px 0px 0px"
            unique_identifier="create_startup_command"
            languages={['shell']}
            value={form.startup_command}
            height="400px"
            padding="0px"
            min_height="172px"
            onChange={(item: string) => onChange({ startup_command: item })}
            font_size={13}
          />
        </div>
        <div className={cn.buttonContainer}>
          <Button type="submit" button_size="medium" width={115} margin="10px 0px 0px 0px">
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
};
